import React, { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid,
} from "recharts";
import Breadcrumb from "../common/breadcrumb";
import { Container } from "reactstrap";
import { C, PIE_COLORS, tbl, reportingGlobalCSS } from "./reportingStyles";
import { GradientDefs, ChartTooltip, Tile, RCard, Badge, Loader } from "./ReportingShared";
import { getReportingStepPickupAnalytics, getReportingNotReturnedCustomers } from "../../utils/apiCalls";
import { generateStepPickupReport, INVOICE_COLUMNS } from "./stepPickupReportPdf";
import { isMerchantScoped } from "../../constants/roles";

// ─── Date preset helpers ─────────────────────────────────
const today = () => new Date().toISOString().split("T")[0];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0]; };
const yearStart = () => `${new Date().getFullYear()}-01-01`;

const DATE_PRESETS = [
  { label: "Last 7 Days", value: "7d", range: () => [daysAgo(7), today()] },
  { label: "Last 30 Days", value: "30d", range: () => [daysAgo(30), today()] },
  { label: "This Month", value: "month", range: () => [monthStart(), today()] },
  { label: "This Year", value: "year", range: () => [yearStart(), today()] },
  { label: "All Time", value: "all", range: () => [null, null] },
];

const fieldLabel = { fontSize: 11, fontWeight: 600, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 };
const dateInput = { padding: "7px 10px", borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 12, fontFamily: "inherit", outline: "none", color: C.text };

// ─── react-select theme (matches ProductPerformancePage) ─────────────────
const selectStyles = {
  control: (base, state) => ({
    ...base, minHeight: 36, borderRadius: 10, borderColor: state.isFocused ? C.coral : C.border,
    boxShadow: state.isFocused ? `0 0 0 1px ${C.coral}` : "none", fontSize: 13, fontFamily: "inherit",
    "&:hover": { borderColor: C.coral },
  }),
  option: (base, state) => ({
    ...base, fontSize: 13, padding: "8px 12px",
    backgroundColor: state.isSelected ? C.coralLight : state.isFocused ? "#f0fdfa" : "white",
    color: state.isSelected ? C.coral : C.text,
  }),
  multiValue: (base) => ({ ...base, borderRadius: 6, background: C.coralLight }),
  multiValueLabel: (base) => ({ ...base, color: C.coral, fontWeight: 600, fontSize: 12 }),
  multiValueRemove: (base) => ({ ...base, color: C.coral, ":hover": { background: C.coral, color: "white" } }),
  placeholder: (base) => ({ ...base, color: C.textLight, fontSize: 13 }),
  menu: (base) => ({ ...base, borderRadius: 10, boxShadow: C.shadowMd, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// Order natures selectable for this report (admin-only combine control).
const ORDER_TYPE_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "loyalty", label: "Loyalty" },
  { value: "thirdParty", label: "Third Party" },
];
const NATURE_LABEL = { normal: "Normal", loyalty: "Loyalty", thirdParty: "Third Party", waiter: "Waiter" };

// All Order.status values (mirrors the backend enum) — for the invoice status filter.
const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "accepted", label: "Accepted" },
  { value: "shipped", label: "Shipped" },
  { value: "ready", label: "Ready" },
  { value: "pickup", label: "Pickup" },
  { value: "delivered", label: "Delivered" },
  { value: "canceled", label: "Canceled" },
  { value: "returned", label: "Returned" },
];

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const sar = (n) => `${round2(n).toLocaleString()} SAR`;

const StepPickupAnalyticsPage = () => {
  const userType = localStorage.getItem("userType");
  const isAdmin = userType === "admin";
  const merchantIdFromStorage = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = isMerchantScoped(userType) ? merchantIdFromStorage : reduxSelectedMerchant;

  // Merchant users can be blocked from this report via a per-merchant flag.
  const reportHiddenForMerchant =
    userType === "merchant" && JSON.parse(localStorage.getItem("hideAppOrdersAnalytics") || "false");

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);

  // Filter state
  const [datePreset, setDatePreset] = useState("30d");
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [churnDays, setChurnDays] = useState(60);
  // Order-type scope (admin-only control; merchants stay normal-only).
  const [orderTypes, setOrderTypes] = useState([ORDER_TYPE_OPTIONS[0]]);

  // Commission invoice (admin-only)
  const [invoiceStatuses, setInvoiceStatuses] = useState([{ value: "delivered", label: "Delivered" }]);
  const [uniformRate, setUniformRate] = useState(true);
  const [uniformRateValue, setUniformRateValue] = useState(0);
  const [rates, setRates] = useState({}); // { nature: percentage }
  const [hideScope, setHideScope] = useState(false); // hide order-type scope on the generated report

  // Not-returned customers preview
  const [nrOpen, setNrOpen] = useState(false);
  const [nrLoading, setNrLoading] = useState(false);
  const [nrList, setNrList] = useState([]);

  // ─── Commission invoice: apply the (per-type or uniform) % to the
  // backend-provided PRE-VAT net per order type (subtotalNet − discountNet;
  // shipping and VAT are informational, never commissionable). Recomputes
  // locally on rate edits — no refetch needed. With no billable status
  // selected there is no defined scope, so no rows (blocked state, never a
  // silent delivered-only fallback). ───────────────────────────────────
  const invoiceRows = useMemo(() => {
    if (!invoiceStatuses.length) return [];
    return (data?.orders?.invoiceBreakdown || []).map(r => {
      const rate = uniformRate ? (Number(uniformRateValue) || 0) : (Number(rates[r.nature]) || 0);
      return { ...r, rate, commission: round2((r.net * rate) / 100) };
    });
  }, [data, uniformRate, uniformRateValue, rates, invoiceStatuses]);

  const invoiceTotals = useMemo(() => invoiceRows.reduce((a, r) => ({
    orders: a.orders + (r.orders || 0),
    sales: a.sales + (r.sales || 0),
    discount: a.discount + (r.discount || 0),
    net: a.net + (r.net || 0),
    tax: a.tax + (r.tax || 0),
    shipping: a.shipping + (r.shipping || 0),
    total: a.total + (r.total || 0),
    commission: a.commission + (r.commission || 0),
  }), { orders: 0, sales: 0, discount: 0, net: 0, tax: 0, shipping: 0, total: 0, commission: 0 }), [invoiceRows]);

  const fetchData = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") return;
    // No order types selected = no defined scope. Block the fetch instead of
    // letting the backend default to normal-only under an empty selector.
    if (isAdmin && !orderTypes.length) return;
    setLoading(true);
    try {
      const filters = { churnDays };
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      // Order-type scope is admin-controlled; merchants always resolve to normal-only.
      const natures = (isAdmin ? orderTypes : [ORDER_TYPE_OPTIONS[0]]).map(o => o.value).join(",");
      filters.orderNatures = natures;
      // Invoice status filter only matters for the admin commission invoice.
      if (isAdmin && invoiceStatuses.length) filters.invoiceStatuses = invoiceStatuses.map(o => o.value).join(",");
      const res = await getReportingStepPickupAnalytics(selectedMerchant, filters);
      if (res) setData(res);
    } catch (e) { console.error("Failed to fetch StepPickup analytics:", e); }
    setLoading(false);
  }, [selectedMerchant, startDate, endDate, churnDays, isAdmin, orderTypes, invoiceStatuses]);

  // Debounced fetch — coalesces rapid filter changes (typing churn days,
  // toggling presets) into a single request ~500ms after the last change.
  useEffect(() => {
    if (!selectedMerchant || selectedMerchant === "all") return;
    const t = setTimeout(() => { fetchData(); }, 500);
    return () => clearTimeout(t);
  }, [fetchData, selectedMerchant]);

  // Preview the customers behind the "Not Returned" KPI (fetched on open,
  // using the current churn window so the list always matches the number).
  const openNotReturned = async () => {
    if (!selectedMerchant || selectedMerchant === "all") return;
    if (isAdmin && !orderTypes.length) return; // no scope selected — see fetchData
    setNrOpen(true);
    setNrLoading(true);
    try {
      const natures = (isAdmin ? orderTypes : [ORDER_TYPE_OPTIONS[0]]).map(o => o.value).join(",");
      const res = await getReportingNotReturnedCustomers(selectedMerchant, churnDays, natures);
      setNrList(res?.customers || []);
    } catch (e) { console.error("Failed to fetch not-returned customers:", e); }
    setNrLoading(false);
  };

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    const found = DATE_PRESETS.find(p => p.value === preset);
    if (found) {
      const [s, e] = found.range();
      setStartDate(s || "");
      setEndDate(e || "");
    }
  };

  // ─── PDF export (generates a structured document, not a screenshot) ──
  const exportPDF = async () => {
    if (!data) return;
    setExporting(true);
    try {
      generateStepPickupReport(data, {
        showInvoice: isAdmin,
        invoiceRows,
        invoiceTotals,
        invoiceStatuses: invoiceStatuses.map(s => s.label),
        hideScope,
      });
    } catch (e) {
      console.error("PDF export failed:", e);
    }
    setExporting(false);
  };

  // Per-merchant hide flag blocks the merchant's own login (admin unaffected).
  if (reportHiddenForMerchant) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!selectedMerchant || selectedMerchant === "all") {
    return (
      <Fragment>
        <Breadcrumb title="StepPickup Analytics" parent="Reporting" />
        <Container fluid>
          <div className="rpt-wrap" style={{ padding: 40, textAlign: "center", color: C.textMed }}>
            <style>{reportingGlobalCSS}</style>
            Please select a merchant to view analytics.
          </div>
        </Container>
      </Fragment>
    );
  }

  const summary = data?.summary || {};
  const customers = data?.customers || {};
  const orders = data?.orders || {};
  const branches = data?.branches || {};
  const products = data?.products || {};
  const meta = data?.meta || {};

  // Chart-ready data
  const ordersPerMonth = (orders.ordersPerMonth || []).map(d => ({ name: d.period, orders: d.count }));
  const ordersPerWeek = (orders.ordersPerWeek || []).map(d => ({ name: d.period, orders: d.count }));
  const ordersByDow = orders.ordersByDayOfWeek || [];
  const ordersByHour = (orders.ordersByHour || []).map(d => ({ name: `${d.hour}h`, orders: d.count }));
  const newCustPerMonth = (customers.newCustomersPerMonth || []).map(d => ({ name: d.period, customers: d.count }));
  const statusData = (orders.statusBreakdown || []).map(d => ({ name: d.status, value: d.count }));
  const typeData = (orders.orderTypeBreakdown || []).map(d => ({ name: d.type, value: d.count }));
  const lifecycleData = [
    { name: "New (period)", value: summary.newCustomers || 0 },
    { name: "Returning", value: summary.returningCustomers || 0 },
  ];
  const branchPerf = branches.performance || [];
  const topProducts = products.top || [];
  const categories = products.categories || [];
  const catData = categories.map(c => ({ name: c.category, value: c.revenue }));

  return (
    <Fragment>
      <Breadcrumb title="StepPickup Analytics" parent="Reporting" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">

          {/* ─── Filter Bar ─────────────────────────────── */}
          <RCard style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
              <div style={{ minWidth: 140 }}>
                <div style={fieldLabel}>Date Range</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {DATE_PRESETS.map(p => (
                    <div key={p.value} onClick={() => handlePresetChange(p.value)}
                      style={{
                        padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: datePreset === p.value ? 700 : 500,
                        cursor: "pointer", border: datePreset === p.value ? `1.5px solid ${C.coral}` : `1.5px solid ${C.border}`,
                        background: datePreset === p.value ? C.coralLight : C.white,
                        color: datePreset === p.value ? C.coral : C.textMed, whiteSpace: "nowrap",
                      }}>{p.label}</div>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div>
                  <div style={fieldLabel}>From</div>
                  <input type="date" value={startDate} style={dateInput}
                    onChange={e => { setStartDate(e.target.value); setDatePreset("custom"); }} />
                </div>
                <div>
                  <div style={fieldLabel}>To</div>
                  <input type="date" value={endDate} style={dateInput}
                    onChange={e => { setEndDate(e.target.value); setDatePreset("custom"); }} />
                </div>
                <div>
                  <div style={fieldLabel}>Churn window (days)</div>
                  <input type="number" min={1} value={churnDays} style={{ ...dateInput, width: 90 }}
                    onChange={e => setChurnDays(parseInt(e.target.value) || 60)} />
                </div>
              </div>

              {isAdmin && (
                <div style={{ minWidth: 260 }}>
                  <div style={fieldLabel}>Order Types</div>
                  <Select
                    isMulti
                    options={ORDER_TYPE_OPTIONS}
                    value={orderTypes}
                    onChange={val => setOrderTypes(val || [])}
                    placeholder="Normal"
                    styles={selectStyles}
                    closeMenuOnSelect={false}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  />
                  {orderTypes.length === 0 && (
                    <div style={{ fontSize: 11, color: C.orange, fontWeight: 600, marginTop: 4 }}>
                      Select at least one order type — report paused, showing last selection.
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginLeft: "auto" }}>
                <button onClick={exportPDF} disabled={exporting || loading || !data}
                  style={{
                    padding: "9px 18px", borderRadius: 10, border: "none", cursor: exporting || loading ? "not-allowed" : "pointer",
                    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white, fontWeight: 700, fontSize: 13,
                    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)", opacity: exporting || loading || !data ? 0.6 : 1,
                  }}>
                  {exporting ? "Generating…" : "⬇ Download PDF"}
                </button>
              </div>
            </div>
          </RCard>

          {/* ─── Commission Invoice (admin only) ─────────── */}
          {isAdmin && (
            <RCard title="🧾 Commission Invoice" style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ minWidth: 260, flex: "0 1 340px" }}>
                  <div style={fieldLabel}>Billable Order Statuses</div>
                  <Select
                    isMulti
                    options={STATUS_OPTIONS}
                    value={invoiceStatuses}
                    onChange={val => setInvoiceStatuses(val || [])}
                    placeholder="Delivered"
                    styles={selectStyles}
                    closeMenuOnSelect={false}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  />
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: C.textMed, cursor: "pointer", paddingBottom: 8 }}>
                  <input type="checkbox" checked={uniformRate} onChange={e => setUniformRate(e.target.checked)} />
                  Same % for all types
                </label>
                {uniformRate ? (
                  <div>
                    <div style={fieldLabel}>Commission %</div>
                    <input type="number" min={0} step="0.1" value={uniformRateValue} style={{ ...dateInput, width: 110 }}
                      onChange={e => setUniformRateValue(e.target.value)} />
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {orderTypes.map(o => (
                      <div key={o.value}>
                        <div style={fieldLabel}>{NATURE_LABEL[o.value] || o.value} %</div>
                        <input type="number" min={0} step="0.1" value={rates[o.value] ?? ""} placeholder="0" style={{ ...dateInput, width: 100 }}
                          onChange={e => setRates(prev => ({ ...prev, [o.value]: e.target.value }))} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {invoiceStatuses.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: C.orange, fontSize: 13, fontWeight: 600, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
                  Select at least one billable order status to build the invoice.
                </div>
              ) : (
                <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden", overflowX: "auto" }}>
                  <table style={tbl.table}>
                    <thead><tr>{INVOICE_COLUMNS.map(c => <th key={c.key} style={tbl.th}>{c.label}</th>)}</tr></thead>
                    <tbody>
                      {invoiceRows.length === 0 ? (
                        <tr><td style={{ ...tbl.td, textAlign: "center", color: C.textMed }} colSpan={INVOICE_COLUMNS.length}>No orders for the selected scope &amp; statuses.</td></tr>
                      ) : (<>
                        {invoiceRows.map(r => (
                          <tr key={r.nature} className="rpt-row">
                            <td style={{ ...tbl.td, fontWeight: 700 }}>{NATURE_LABEL[r.nature] || r.nature}</td>
                            <td style={{ ...tbl.td, fontWeight: 600 }}>{r.orders}</td>
                            <td style={tbl.td}>{sar(r.sales)}</td>
                            <td style={tbl.td}>{sar(r.discount)}</td>
                            <td style={{ ...tbl.td, fontWeight: 600 }}>{sar(r.net)}</td>
                            <td style={tbl.td}>{sar(r.tax)}</td>
                            <td style={tbl.td}>{sar(r.shipping)}</td>
                            <td style={tbl.td}>{sar(r.total)}</td>
                            <td style={tbl.td}>{r.rate}%</td>
                            <td style={{ ...tbl.td, fontWeight: 700, color: C.coral }}>{sar(r.commission)}</td>
                          </tr>
                        ))}
                        <tr style={{ background: C.coralLight }}>
                          <td style={{ ...tbl.td, fontWeight: 800 }}>Grand Total</td>
                          <td style={{ ...tbl.td, fontWeight: 800 }}>{invoiceTotals.orders}</td>
                          <td style={{ ...tbl.td, fontWeight: 800 }}>{sar(invoiceTotals.sales)}</td>
                          <td style={{ ...tbl.td, fontWeight: 800 }}>{sar(invoiceTotals.discount)}</td>
                          <td style={{ ...tbl.td, fontWeight: 800 }}>{sar(invoiceTotals.net)}</td>
                          <td style={{ ...tbl.td, fontWeight: 800 }}>{sar(invoiceTotals.tax)}</td>
                          <td style={{ ...tbl.td, fontWeight: 800 }}>{sar(invoiceTotals.shipping)}</td>
                          <td style={{ ...tbl.td, fontWeight: 800 }}>{sar(invoiceTotals.total)}</td>
                          <td style={tbl.td}></td>
                          <td style={{ ...tbl.td, fontWeight: 800, color: C.coral }}>{sar(invoiceTotals.commission)}</td>
                        </tr>
                      </>)}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ fontSize: 11.5, color: C.textMed, marginTop: 8 }}>
                All amounts exclude VAT except <b>VAT (included)</b> — the tax portion inside Total — and <b>Total</b> (what customers actually paid, incl. VAT &amp; shipping).
                Commission = Rate % × <b>Net</b> (pre-VAT sales − pre-VAT discounts; shipping excluded).
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.textMed, marginTop: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={hideScope} onChange={e => setHideScope(e.target.checked)} />
                Hide order-type scope on the generated report (for the client copy)
              </label>
            </RCard>
          )}

          {loading ? <Loader /> : (
            <div style={{ background: C.bg, padding: 4 }}>

              {/* Report header (shown in PDF) */}
              <div style={{ marginBottom: 16, padding: "4px 8px" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>
                  {meta.merchantName || "StepPickup"} — Analytics Report
                </div>
                <div style={{ fontSize: 12, color: C.textMed, marginTop: 4 }}>
                  {meta.period ? `${meta.period.startDateLocal || (meta.period.startDate || "").split("T")[0]} → ${meta.period.endDateLocal || (meta.period.endDate || "").split("T")[0]}` : ""}
                  {meta.timezone ? ` · ${meta.timezone}` : ""}
                  {!hideScope && ` · App orders (${meta.orderScope || "orderNature='normal'"})`}
                </div>
              </div>

              {/* ─── Summary KPIs ───────────────────────── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16, marginBottom: 20 }}>
                <Tile label="Total Orders" value={summary.totalOrders ?? 0} sub={`${summary.successOrders ?? 0} successful`} icon="🧾" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                <Tile label="Revenue" value={`${summary.totalRevenue ?? 0} SAR`} sub={`AOV ${summary.avgOrderValue ?? 0} SAR`} icon="💰" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={60} />
                <Tile label="Cancelled" value={summary.cancelledOrders ?? 0} sub={`${summary.cancellationRate ?? 0}% rate`} icon="✖️" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={120} />
                <Tile label="Active Customers" value={summary.activeCustomers ?? 0} sub={`${summary.newCustomers ?? 0} new · ${summary.returningCustomers ?? 0} returning`} icon="👥" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={180} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16, marginBottom: 24 }}>
                <Tile label="Lifetime Customers" value={customers.lifetimeCustomers ?? 0} sub={`${customers.repeatRate ?? 0}% repeat rate`} icon="📇" gradient={`linear-gradient(135deg, ${C.purple}, #C39BD3)`} delay={0} />
                <Tile label="Repeating" value={customers.repeatingCustomers ?? 0} sub={`${customers.oneTimeCustomers ?? 0} one-time`} icon="🔁" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={60} />
                <div onClick={openNotReturned} style={{ cursor: "pointer" }} title="Click to preview these customers">
                  <Tile label="Not Returned" value={customers.notReturnedCustomers ?? 0} sub={`No order in ${meta.churnDays ?? churnDays} days · Click to preview`} icon="💤" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={120} />
                </div>
                <Tile label="Favourite" value={products.favouriteProduct || "-"} sub={branches.favouriteBranch ? `Branch: ${branches.favouriteBranch}` : ""} icon="⭐" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={180} />
              </div>

              {/* ─── Orders over time ───────────────────── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 16, marginBottom: 20 }}>
                <RCard title="📈 Orders per Month">
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={ordersPerMonth}>
                      <GradientDefs />
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.textMed }} axisLine={{ stroke: C.border }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Area type="monotone" dataKey="orders" stroke={C.coral} fill="url(#gCoral)" strokeWidth={2} dot={{ r: 2, fill: C.coral }} name="Orders" />
                    </AreaChart>
                  </ResponsiveContainer>
                </RCard>
                <RCard title="📊 Orders per Week">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={ordersPerWeek}>
                      <GradientDefs />
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: C.textMed }} axisLine={{ stroke: C.border }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="orders" fill="url(#gBlueBar)" radius={[6, 6, 0, 0]} name="Orders" />
                    </BarChart>
                  </ResponsiveContainer>
                </RCard>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 16, marginBottom: 20 }}>
                <RCard title="📅 Orders by Day of Week">
                  <ResponsiveContainer width="100%" height={230}>
                    <BarChart data={ordersByDow}>
                      <GradientDefs />
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.textMed }} axisLine={{ stroke: C.border }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="count" fill="url(#gCoralBar)" radius={[6, 6, 0, 0]} name="Orders" />
                    </BarChart>
                  </ResponsiveContainer>
                </RCard>
                <RCard title="🕐 Peak Hours (orders by hour)">
                  <ResponsiveContainer width="100%" height={230}>
                    <AreaChart data={ordersByHour}>
                      <GradientDefs />
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="name" tick={{ fontSize: 9, fill: C.textMed }} interval={1} axisLine={{ stroke: C.border }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Area type="monotone" dataKey="orders" stroke={C.green} fill="url(#gGreen)" strokeWidth={2} name="Orders" />
                    </AreaChart>
                  </ResponsiveContainer>
                </RCard>
              </div>

              {/* ─── Customers + distributions ──────────── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16, marginBottom: 20 }}>
                <RCard title="🧍 New Customers per Month">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={newCustPerMonth}>
                      <GradientDefs />
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.textMed }} axisLine={{ stroke: C.border }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="customers" fill="url(#gGreen)" radius={[6, 6, 0, 0]} name="New customers" />
                    </BarChart>
                  </ResponsiveContainer>
                </RCard>
                <RCard title="🥧 New vs Returning">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={lifecycleData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={4} cornerRadius={6}
                        label={({ name, value }) => `${name}: ${value}`}>
                        {lifecycleData.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </RCard>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16, marginBottom: 20 }}>
                <RCard title="🍩 Order Status Breakdown">
                  <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3} cornerRadius={6}
                        label={({ name, value }) => `${name} (${value})`}>
                        {statusData.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </RCard>
                <RCard title="🛍️ Order Type Breakdown">
                  <ResponsiveContainer width="100%" height={230}>
                    <PieChart>
                      <Pie data={typeData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3} cornerRadius={6}
                        label={({ name, value }) => `${name} (${value})`}>
                        {typeData.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </RCard>
              </div>

              {/* ─── Products ───────────────────────────── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 16, marginBottom: 20 }}>
                <RCard title="🔥 Top Products by Quantity">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={topProducts.slice(0, 8).map(p => ({ name: p.title, qty: p.qtySold }))} layout="vertical">
                      <GradientDefs />
                      <XAxis type="number" tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="qty" fill="url(#gCoralBar)" radius={[0, 8, 8, 0]} name="Units" barSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </RCard>
                <RCard title="🍩 Revenue by Category">
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={catData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={4} cornerRadius={6}
                        label={({ name, value }) => `${name} (${value})`}>
                        {catData.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </RCard>
              </div>

              {/* ─── Branch performance table ───────────── */}
              <RCard title="🏪 Branch Performance">
                <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden", overflowX: "auto" }}>
                  <table style={tbl.table}>
                    <thead><tr>{["Branch", "Orders", "Successful", "Cancelled", "Cancel %", "Revenue (SAR)"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                    <tbody>{branchPerf.map(b => (
                      <tr key={b.branchId} className="rpt-row">
                        <td style={{ ...tbl.td, fontWeight: 700 }}>{b.branch}</td>
                        <td style={{ ...tbl.td, fontWeight: 600 }}>{b.orders}</td>
                        <td style={tbl.td}>{b.successOrders}</td>
                        <td style={tbl.td}>{b.cancelledOrders}</td>
                        <td style={tbl.td}>
                          <Badge color={b.cancellationRate > 15 ? C.coral : C.green} bg={b.cancellationRate > 15 ? C.coralLight : C.greenLight}>{b.cancellationRate}%</Badge>
                        </td>
                        <td style={{ ...tbl.td, fontWeight: 700 }}>{b.revenue} SAR</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </RCard>

              {/* ─── Top products table ─────────────────── */}
              <RCard title="📦 Top Products">
                <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden", overflowX: "auto" }}>
                  <table style={tbl.table}>
                    <thead><tr>{["#", "Product", "Qty Sold", "Revenue (SAR)"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                    <tbody>{topProducts.map((p, i) => (
                      <tr key={p.productId} className="rpt-row">
                        <td style={tbl.td}>{i + 1}</td>
                        <td style={{ ...tbl.td, fontWeight: 700 }}>{p.title}</td>
                        <td style={{ ...tbl.td, fontWeight: 600 }}>{p.qtySold}</td>
                        <td style={{ ...tbl.td, fontWeight: 700 }}>{p.revenue} SAR</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </RCard>

            </div>
          )}

          {/* ─── Not-returned customers preview modal ──── */}
          {nrOpen && (
            <div onClick={() => setNrOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
              <div onClick={e => e.stopPropagation()} className="rpt-fade"
                style={{ background: C.white, borderRadius: 16, width: "min(900px, 100%)", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: C.shadowLg, overflow: "hidden" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: `1px solid ${C.border}` }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Not Returned Customers</div>
                    <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>No order in {churnDays} days · {nrLoading ? "loading…" : `${nrList.length} customers`}</div>
                  </div>
                  <div onClick={() => setNrOpen(false)} style={{ cursor: "pointer", fontSize: 22, color: C.textLight, lineHeight: 1, padding: 4 }}>×</div>
                </div>
                <div style={{ overflowY: "auto", padding: 16 }}>
                  {nrLoading ? <Loader /> : nrList.length === 0 ? (
                    <div style={{ padding: 30, textAlign: "center", color: C.textMed }}>No churned customers for this window. 🎉</div>
                  ) : (
                    <table style={tbl.table}>
                      <thead><tr>{["Customer", "Phone", "Orders", "Total Spent", "Last Order", "Days Ago"].map(h => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
                      <tbody>{nrList.map(c => (
                        <tr key={c.userId} className="rpt-row">
                          <td style={{ ...tbl.td, fontWeight: 700 }}>{c.name}</td>
                          <td style={tbl.td}>{c.phone || "-"}</td>
                          <td style={{ ...tbl.td, fontWeight: 600 }}>{c.totalOrders}</td>
                          <td style={{ ...tbl.td, fontWeight: 600 }}>{c.totalSpent} SAR</td>
                          <td style={tbl.td}>{c.lastOrder || "-"}</td>
                          <td style={tbl.td}>
                            <Badge color={c.daysSinceLastOrder > 120 ? C.coral : C.orange} bg={c.daysSinceLastOrder > 120 ? C.coralLight : C.orangeLight}>{c.daysSinceLastOrder}d</Badge>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Container>
    </Fragment>
  );
};

export default StepPickupAnalyticsPage;
